"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAuth = exports.AuthProvider = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * AuthProvider manages authentication state and provides auth methods
 */
const react_1 = require("react");
const authApi = __importStar(require("../helpers/authApi"));
const apiClient_1 = require("../helpers/apiClient");
const AuthContext = (0, react_1.createContext)(undefined);
/**
 * SECURITY NOTE: JWT tokens are stored in sessionStorage.
 *
 * sessionStorage provides a balance between security and user experience:
 * - Tokens persist across page refreshes (good UX)
 * - Tokens are cleared when the browser tab/window closes (reasonable security)
 * - XSS vulnerabilities can still access sessionStorage (requires secure coding practices)
 *
 * TRADE-OFFS:
 * - sessionStorage is accessible to XSS attacks, but so is the DOM and memory
 * - Users stay authenticated within a browser session (expected behavior)
 * - For maximum security, httpOnly cookies would be ideal but require backend changes:
 *   - int-platform-auth service must set httpOnly cookies in login response
 *   - CORS configuration for credentials
 *   - CSRF protection implementation
 *
 * Current approach: Tokens stored in sessionStorage, cleared on tab close.
 * This balances OWASP recommendations with practical UX requirements.
 */
const TOKEN_STORAGE_KEY = 'degreeplan_auth_token';
const USERNAME_STORAGE_KEY = 'degreeplan_auth_username';
/**
 * AuthProvider component that manages authentication state
 * @param {AuthProviderProps} props - Component properties
 * @returns {React.FC<AuthProviderProps>} The AuthProvider component
 */
const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = (0, react_1.useState)({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: true,
        error: null,
    });
    // SECURITY: Prevent concurrent token refresh attempts (race condition protection)
    const refreshPromiseRef = (0, react_1.useRef)(null);
    /**
     * Load user data from token
     */
    const loadUserFromToken = (0, react_1.useCallback)((token_1, ...args_1) => __awaiter(void 0, [token_1, ...args_1], void 0, function* (token, fetchPermissions = true) {
        try {
            const decoded = authApi.decodeToken(token);
            // Optionally fetch permissions (skip during initial page load to avoid triggering refresh)
            const permissions = fetchPermissions
                ? yield authApi.getTenantPermissions(token)
                : [];
            const user = {
                id: decoded.userId,
                // JWT from this backend does not include a username claim.
                // Fall back to the value stored at login time (sessionStorage).
                username: decoded.username || sessionStorage.getItem(USERNAME_STORAGE_KEY) || '',
                type: decoded.userType,
                tenantId: decoded.tenantId,
                profileId: decoded.profileId,
                permissions,
            };
            return user;
        }
        catch (error) {
            return null;
        }
    }), []);
    /**
     * Initialize authentication state on mount
     * Attempt to restore session from sessionStorage
     */
    (0, react_1.useEffect)(() => {
        const initAuth = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const storedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
                if (storedToken) {
                    const tokenIsExpired = authApi.isTokenExpired(storedToken);
                    if (tokenIsExpired) {
                        // Try to refresh the expired token
                        const refreshedToken = yield authApi.refreshToken(storedToken);
                        if (refreshedToken) {
                            // loadUserFromToken fetches permissions; returns null on any failure
                            const user = yield loadUserFromToken(refreshedToken);
                            if (user) {
                                sessionStorage.setItem(TOKEN_STORAGE_KEY, refreshedToken);
                                setAuthState({ isAuthenticated: true, user, token: refreshedToken, loading: false, error: null });
                                return;
                            }
                        }
                    }
                    else {
                        // Token still valid — restore session including permissions.
                        // loadUserFromToken returns null if the permission fetch fails (e.g. 403
                        // after a backend password change), so we never enter an authenticated-but-
                        // no-permissions limbo state.
                        const user = yield loadUserFromToken(storedToken);
                        if (user) {
                            setAuthState({ isAuthenticated: true, user, token: storedToken, loading: false, error: null });
                            return;
                        }
                    }
                    // Restore failed — clear stale token so the user reaches the login page cleanly
                    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
                }
            }
            catch (_a) {
                sessionStorage.removeItem(TOKEN_STORAGE_KEY);
            }
            setAuthState((prev) => (Object.assign(Object.assign({}, prev), { loading: false })));
        });
        initAuth();
    }, [loadUserFromToken]);
    /**
     * Logout and clear authentication state.
     * Pass an optional message to display on the login page (e.g. session expired, inactivity).
     */
    const logout = (0, react_1.useCallback)((message) => {
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(USERNAME_STORAGE_KEY);
        setAuthState({
            isAuthenticated: false,
            user: null,
            token: null,
            loading: false,
            error: message || null,
        });
    }, []);
    /**
     * Wire up the apiClient token getter once on mount.
     *
     * Reading from sessionStorage (rather than closing over authState.token) means
     * the getter is always fresh — no React effect timing race where child component
     * effects fire API calls before this parent effect runs to update the closure.
     */
    (0, react_1.useEffect)(() => {
        apiClient_1.apiClient.setTokenGetter(() => sessionStorage.getItem(TOKEN_STORAGE_KEY));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    /**
     * Initialize API client token-refresh handler.
     *
     * When the API returns 401 and a token refresh fails, we immediately log the
     * user out with a session-expired message so they are redirected to login
     * rather than seeing a raw API error.
     *
     * SECURITY: Race condition protection via refreshPromiseRef — if multiple API
     * calls get 401 simultaneously, only one refresh attempt is made.
     *
     * NOTE: The 401 refresh path is skipped entirely when apiClient.tokenOverride
     * is active (Salesforce iframe), so an invalid iframe token never triggers a
     * session-wide logout.
     */
    (0, react_1.useEffect)(() => {
        apiClient_1.apiClient.setTokenRefresher(() => __awaiter(void 0, void 0, void 0, function* () {
            if (!authState.token)
                return null;
            // SECURITY: If refresh is already in progress, wait for it instead of starting a new one
            if (refreshPromiseRef.current) {
                return refreshPromiseRef.current;
            }
            refreshPromiseRef.current = (() => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    const newToken = yield authApi.refreshToken(authState.token);
                    if (newToken) {
                        const user = yield loadUserFromToken(newToken);
                        if (user) {
                            sessionStorage.setItem(TOKEN_STORAGE_KEY, newToken);
                            setAuthState(prev => (Object.assign(Object.assign({}, prev), { token: newToken, user, isAuthenticated: true })));
                            return newToken;
                        }
                    }
                    // Refresh returned null or user load failed — session is expired
                    logout('Your session has expired. Please sign in again.');
                    return null;
                }
                catch (_a) {
                    // Refresh threw — session is expired or server is unreachable
                    logout('Your session has expired. Please sign in again.');
                    return null;
                }
                finally {
                    refreshPromiseRef.current = null;
                }
            }))();
            return refreshPromiseRef.current;
        }));
    }, [authState.token, loadUserFromToken, logout]);
    /**
     * Login with username and password
     */
    const login = (0, react_1.useCallback)((username, password) => __awaiter(void 0, void 0, void 0, function* () {
        setAuthState((prev) => (Object.assign(Object.assign({}, prev), { loading: true, error: null })));
        try {
            const token = yield authApi.login(username, password);
            const user = yield loadUserFromToken(token);
            if (!user) {
                throw new Error('Failed to load user data');
            }
            // Store token BEFORE setting state so apiClient.getToken() (which reads
            // sessionStorage) returns the correct token the instant isAuthenticated
            // flips to true and child components start making API calls.
            sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
            sessionStorage.setItem(USERNAME_STORAGE_KEY, username);
            setAuthState({
                isAuthenticated: true,
                user,
                token,
                loading: false,
                error: null,
            });
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Login failed';
            setAuthState({
                isAuthenticated: false,
                user: null,
                token: null,
                loading: false,
                error: errorMessage,
            });
            throw error;
        }
    }), [loadUserFromToken]);
    /**
     * Check if user has a specific permission
     */
    const hasPermission = (0, react_1.useCallback)((permissionCode) => {
        if (!authState.user)
            return false;
        return authState.user.permissions.some((perm) => perm.code === permissionCode);
    }, [authState.user]);
    /**
     * Check if user has any of the specified permissions
     */
    const hasAnyPermission = (0, react_1.useCallback)((...permissionCodes) => {
        if (!authState.user)
            return false;
        return permissionCodes.some((code) => hasPermission(code));
    }, [authState.user, hasPermission]);
    /**
     * Check if user has all of the specified permissions
     */
    const hasAllPermissions = (0, react_1.useCallback)((...permissionCodes) => {
        if (!authState.user)
            return false;
        return permissionCodes.every((code) => hasPermission(code));
    }, [authState.user, hasPermission]);
    const value = (0, react_1.useMemo)(() => (Object.assign(Object.assign({}, authState), { login,
        logout,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions })), [authState, login, logout, hasPermission, hasAnyPermission, hasAllPermissions]);
    return (0, jsx_runtime_1.jsx)(AuthContext.Provider, { value: value, children: children });
};
exports.AuthProvider = AuthProvider;
/**
 * Custom hook to access authentication context
 * @returns {AuthContextValue} Authentication context value
 */
const useAuth = () => {
    const context = (0, react_1.useContext)(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
exports.useAuth = useAuth;
